import type { ComponentType } from 'react';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { matchPath } from 'react-router-dom';
import {
  faBriefcase,
  faBuilding,
  faCalendarClock,
  faHome,
  faMoneyBill,
  faMoneyCheckDollar,
  faShop,
  faUserGroup,
  faUsers,
} from '@fortawesome/pro-regular-svg-icons';
import { UserRole } from '@core/generated/gql-types';
import { TemplateTerm } from '@core/template/useTemplateStore';
import type { CoreLoopConfigurationStatus } from '@core/coreLoopConfiguration/useCoreLoopConfigurationStore';
import type { ICoreLoopConfigurationItems } from '@core/coreLoopConfiguration/coreLoopConfiguration.types';
import type { PortalAccessContext, PortalAudience } from '@/access';
import { buildPortalAccessContext, getPortalAudience } from '@/access';
import { type FeatureFlag, isFeatureEnabled } from '@/utils/featureFlags';
import {
  hasAnyPermission,
  Permission,
} from '@core/permission/permission.constants';
import {
  Bills,
  ClientPage,
  Clients,
  ConfirmSignUp,
  Dashboard,
  ForgotPassword,
  JobCreate,
  JobPage,
  Jobs,
  Login,
  Payroll,
  Reports,
  ResetPassword,
  SetNewPassword,
  SignUp,
  Sites,
  Timesheets,
  ClientUsers,
  WorkerPage,
  Workers,
} from '@/pages';

export type PortalRouteDef = {
  id: string;
  path: string;
  component: ComponentType;
  audiences: PortalAudience[];
  adminOnly?: boolean;
  /** Route (and its menu item) is only registered when the flag is enabled. */
  featureFlag?: FeatureFlag;
  /**
   * Backend permissions (any of) required to open the route and see its menu
   * item. Checked once `getPermissionsForCurrentUser` has loaded.
   */
  requiredPermissions?: Permission[];
  /**
   * Tenant core loop configuration flag that must be on to open the route and
   * see its menu item. Checked once the configuration has loaded.
   */
  coreLoopConfigFlag?: CoreLoopConfigFlag;
  menu?: {
    title: string;
    titleTerm?: TemplateTerm;
    titlePlural?: boolean;
    icon: IconDefinition;
    order: number;
    audiences: PortalAudience[];
    adminOnly?: boolean;
  };
};

export type CoreLoopConfigFlag = 'shouldUseBlockTimesheetApproval';

export type CoreLoopConfigAccess = {
  status: CoreLoopConfigurationStatus;
  items: Pick<ICoreLoopConfigurationItems, CoreLoopConfigFlag> | null;
};

const CORE_LOOP_CONFIG_NOT_LOADED: CoreLoopConfigAccess = {
  status: 'idle',
  items: null,
};

export type PublicRouteDef = {
  id: string;
  path: string;
  component: ComponentType;
};

export const DEFAULT_HOME_BY_AUDIENCE: Record<PortalAudience, string> = {
  client: '/jobs',
  tenant: '/dashboard',
};

export const PUBLIC_AUTH_ROUTES: PublicRouteDef[] = [
  { id: 'login', path: '/login', component: Login },
  { id: 'signup', path: '/signup', component: SignUp },
  {
    id: 'forgot-password',
    path: '/forgot-password',
    component: ForgotPassword,
  },
  { id: 'reset-password', path: '/reset-password', component: ResetPassword },
  { id: 'confirm-signup', path: '/confirm-signup', component: ConfirmSignUp },
  {
    id: 'set-new-password',
    path: '/set-new-password',
    component: SetNewPassword,
  },
];

export const PUBLIC_ROUTES: PublicRouteDef[] = [...PUBLIC_AUTH_ROUTES];

export const PROTECTED_APP_ROUTES: PortalRouteDef[] = [
  {
    id: 'dashboard',
    path: '/dashboard',
    component: Dashboard,
    audiences: ['tenant'],
    menu: {
      title: 'Dashboard',
      icon: faHome,
      order: 0,
      audiences: ['tenant'],
    },
  },
  {
    id: 'jobs',
    path: '/jobs',
    component: Jobs,
    audiences: ['client', 'tenant'],
    menu: {
      title: 'Jobs',
      titleTerm: TemplateTerm.JOB,
      titlePlural: true,
      icon: faBriefcase,
      order: 1,
      audiences: ['client', 'tenant'],
    },
  },
  {
    id: 'jobs-new',
    path: '/jobs/new',
    component: JobCreate,
    audiences: ['client', 'tenant'],
  },
  {
    id: 'jobs-detail',
    path: '/jobs/:id',
    component: JobPage,
    audiences: ['client', 'tenant'],
  },
  {
    id: 'timesheets',
    path: '/timesheets',
    component: Timesheets,
    audiences: ['client', 'tenant'],
    coreLoopConfigFlag: 'shouldUseBlockTimesheetApproval',
    requiredPermissions: [Permission.ViewTimesheetsForApproval],
    menu: {
      title: 'Timesheets',
      icon: faCalendarClock,
      order: 1.5,
      audiences: ['client', 'tenant'],
    },
  },
  {
    id: 'sites',
    path: '/sites',
    component: Sites,
    audiences: ['client'],
    adminOnly: true,
    menu: {
      title: 'Sites',
      icon: faShop,
      order: 2,
      audiences: ['client'],
      adminOnly: true,
    },
  },
  {
    id: 'users',
    path: '/users',
    component: ClientUsers,
    audiences: ['client'],
    adminOnly: true,
    menu: {
      title: 'Users',
      icon: faUsers,
      order: 3,
      audiences: ['client'],
      adminOnly: true,
    },
  },
  {
    id: 'workers',
    path: '/workers',
    component: Workers,
    audiences: ['tenant'],
    menu: {
      title: 'Workers',
      titleTerm: TemplateTerm.WORKER,
      titlePlural: true,
      icon: faUserGroup,
      order: 2,
      audiences: ['tenant'],
    },
  },
  {
    id: 'workers-detail',
    path: '/workers/:id',
    component: WorkerPage,
    audiences: ['tenant'],
  },
  {
    id: 'clients',
    path: '/clients',
    component: Clients,
    audiences: ['tenant'],
    menu: {
      title: 'Clients',
      titleTerm: TemplateTerm.CLIENT,
      titlePlural: true,
      icon: faBuilding,
      order: 3,
      audiences: ['tenant'],
    },
  },
  {
    id: 'clients-detail',
    path: '/clients/:id',
    component: ClientPage,
    audiences: ['tenant'],
  },
  {
    id: 'reports',
    path: '/reports',
    component: Reports,
    audiences: ['tenant'],
  },
  {
    id: 'bills',
    path: '/bills',
    component: Bills,
    audiences: [], // TODO: The Bills tab is not ready yet. So no one should see it.
    adminOnly: true,
    menu: {
      title: 'Bills',
      icon: faMoneyBill,
      order: 4,
      audiences: [],
      adminOnly: true,
    },
  },
  {
    id: 'payroll',
    path: '/payroll',
    component: Payroll,
    audiences: ['tenant'],
    featureFlag: 'payrollTab',
    requiredPermissions: [
      Permission.PayrollRunPayrollReadynessReport,
      Permission.PayrollRunPaycycle,
      Permission.PayrollForceSyncExistingPaycycle,
    ],
    menu: {
      title: 'Pay Run',
      icon: faMoneyCheckDollar,
      order: 5,
      audiences: ['tenant'],
    },
  },
];

export type { PortalAccessContext, PortalAudience } from '@/access';
export { buildPortalAccessContext, getPortalAccessFromRole } from '@/access';

const hasAudienceAdminAccess = (
  audience: PortalAudience,
  access: Pick<PortalAccessContext, 'isClientAdmin' | 'isPortalAdmin'>,
): boolean =>
  audience === 'client' ? access.isClientAdmin : access.isPortalAdmin;

const isRouteFeatureEnabled = (route: PortalRouteDef): boolean =>
  !route.featureFlag || isFeatureEnabled(route.featureFlag);

export type RoutePermissionAccess = 'granted' | 'pending' | 'denied';

/**
 * Permission check for routes with `requiredPermissions`. `pending` while
 * permissions are loading; a failed load is `denied` (fail closed).
 */
export const getRoutePermissionAccess = (
  route: PortalRouteDef,
  access: Pick<PortalAccessContext, 'permissions' | 'permissionsStatus'>,
): RoutePermissionAccess => {
  if (!route.requiredPermissions?.length) {
    return 'granted';
  }
  if (
    access.permissionsStatus === 'idle' ||
    access.permissionsStatus === 'loading'
  ) {
    return 'pending';
  }
  return hasAnyPermission(access.permissions, route.requiredPermissions)
    ? 'granted'
    : 'denied';
};

/**
 * Core loop configuration check for routes with `coreLoopConfigFlag`. `pending`
 * while the configuration is loading; a failed load is `denied` (fail closed).
 */
export const getRouteCoreLoopConfigAccess = (
  route: PortalRouteDef,
  coreLoopConfig: CoreLoopConfigAccess,
): RoutePermissionAccess => {
  if (!route.coreLoopConfigFlag) {
    return 'granted';
  }
  if (coreLoopConfig.status === 'idle' || coreLoopConfig.status === 'loading') {
    return 'pending';
  }
  return coreLoopConfig.items?.[route.coreLoopConfigFlag]
    ? 'granted'
    : 'denied';
};

/**
 * Audience, admin and feature-flag checks. Backend permissions are checked
 * separately via `getRoutePermissionAccess` as they load asynchronously.
 */
export const isRouteAccessible = (
  route: PortalRouteDef,
  access: PortalAccessContext,
): boolean => {
  if (!isRouteFeatureEnabled(route)) {
    return false;
  }

  if (!route.audiences.includes(access.audience)) {
    return false;
  }

  if (route.adminOnly && !hasAudienceAdminAccess(access.audience, access)) {
    return false;
  }

  return true;
};

export const getProtectedRoutesForPortal = (
  portalAudience: PortalAudience,
  access: Pick<PortalAccessContext, 'isPortalAdmin' | 'isClientAdmin'>,
): PortalRouteDef[] =>
  PROTECTED_APP_ROUTES.filter(
    (route) =>
      isRouteFeatureEnabled(route) &&
      route.audiences.includes(portalAudience) &&
      (!route.adminOnly || hasAudienceAdminAccess(portalAudience, access)),
  );

/** All protected app routes registered in the single shared router tree. */
export const getRegisteredProtectedRoutes = (
  access: Pick<
    PortalAccessContext,
    'audience' | 'isPortalAdmin' | 'isClientAdmin'
  >,
): PortalRouteDef[] =>
  PROTECTED_APP_ROUTES.filter(
    (route) =>
      isRouteFeatureEnabled(route) &&
      (!route.adminOnly || hasAudienceAdminAccess(access.audience, access)),
  );

export type SidebarMenuItem = {
  id: string;
  path: string;
  icon: IconDefinition;
  title: string;
};

export const getSidebarMenuItems = (
  access: PortalAccessContext,
  getTerm: (term: TemplateTerm, plural: boolean) => string,
  coreLoopConfig: CoreLoopConfigAccess = CORE_LOOP_CONFIG_NOT_LOADED,
): SidebarMenuItem[] => {
  if (!access.isClientPortal && !access.isTenantPortal) {
    return [];
  }

  const audience = access.audience;

  return PROTECTED_APP_ROUTES.filter((route) => {
    if (!isRouteFeatureEnabled(route)) {
      return false;
    }

    if (!route.menu?.audiences.includes(audience)) {
      return false;
    }

    if (route.menu.adminOnly && !hasAudienceAdminAccess(audience, access)) {
      return false;
    }

    return (
      getRoutePermissionAccess(route, access) === 'granted' &&
      getRouteCoreLoopConfigAccess(route, coreLoopConfig) === 'granted'
    );
  })
    .sort((a, b) => (a.menu?.order ?? 0) - (b.menu?.order ?? 0))
    .map((route) => {
      const menu = route.menu!;
      const resolvedTitle = menu.titleTerm
        ? getTerm(menu.titleTerm, menu.titlePlural ?? true) || menu.title
        : menu.title;

      return {
        id: route.id,
        path: route.path,
        icon: menu.icon,
        title: resolvedTitle,
      };
    });
};

export const getDefaultHomePath = (
  access: Pick<PortalAccessContext, 'audience' | 'isClientPortal'>,
): string => DEFAULT_HOME_BY_AUDIENCE[getPortalAudience(access)];

export const findMatchingProtectedRoute = (
  logicalPath: string,
): PortalRouteDef | undefined => {
  const normalizedPath = logicalPath.startsWith('/')
    ? logicalPath
    : `/${logicalPath}`;

  const routesBySpecificity = [...PROTECTED_APP_ROUTES].sort(
    (a, b) => b.path.length - a.path.length,
  );

  return routesBySpecificity.find((route) =>
    matchPath({ path: route.path, end: true }, normalizedPath),
  );
};

/** Redirect when the current path is not accessible for the user's portal role. */
export const getPortalAlignmentRedirect = (
  pathname: string,
  role: UserRole | null | undefined,
): string | undefined => {
  if (!role) {
    return undefined;
  }

  const matchedRoute = findMatchingProtectedRoute(pathname);

  if (!matchedRoute) {
    return undefined;
  }

  const access = buildPortalAccessContext(role);

  if (isRouteAccessible(matchedRoute, access)) {
    return undefined;
  }

  return getDefaultHomePath(access);
};
